import { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, ChevronLeft, ChevronRight, Grid, List, X, Check, Plus, Download, Info, Smartphone, Monitor } from 'lucide-react';
import { usePhotos, useUploadPhotos, useBulkTagPhotos } from '../../hooks/usePhotos';
import { useTags, useCreateTag } from '../../hooks/useTags';
import { Photo } from '../../types';
import PhotoCard from '../shared/PhotoCard';
import PhotoListItem from '../shared/PhotoListItem';
import LoadingSpinner from '../shared/LoadingSpinner';
import Lightbox from '../shared/Lightbox';
import Select from '../shared/Select';
import { useDeviceDetection } from '../../utils/deviceDetection';
import { 
  downloadPhotosOptimizedAdmin, 
  estimateAdminDownloadTime, 
  getAdminDownloadStrategyDescription,
  AdminDownloadProgress 
} from '../../utils/adminDownloadHelpers';
import { useMutation } from 'react-query';
import toast from 'react-hot-toast';

export default function PhotoGallery() {
  const [selectedPhotos, setSelectedPhotos] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    hasTag: undefined as boolean | undefined,
  });
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showUpload, setShowUpload] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [showCreateTagForm, setShowCreateTagForm] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagEmail, setNewTagEmail] = useState('');
  const [lightboxPhoto, setLightboxPhoto] = useState<Photo | null>(null);
  const [downloadProgress, setDownloadProgress] = useState<AdminDownloadProgress | null>(null);
  const [showDownloadInfo, setShowDownloadInfo] = useState(false);
  
  const deviceInfo = useDeviceDetection();

  const { data: photosData, isLoading: photosLoading, error: photosError } = usePhotos({
    page: currentPage,
    limit: 50,
    ...filters,
  });

  const uploadMutation = useUploadPhotos();
  const bulkTagMutation = useBulkTagPhotos();
  const createTagMutation = useCreateTag();
  const { data: tagsData } = useTags({});

  // Admin download mutation with device-specific optimization
  const adminDownloadMutation = useMutation(
    async (photoIds: number[]) => {
      if (!photosData?.data) {
        throw new Error('Données photos manquantes pour le téléchargement');
      }
      
      const selectedPhotoObjects = photosData.data.filter(photo => 
        photoIds.includes(photo.id)
      );
      
      return downloadPhotosOptimizedAdmin(
        selectedPhotoObjects,
        setDownloadProgress
      );
    },
    {
      onMutate: () => {
        setDownloadProgress({ completed: 0, total: selectedPhotos.length, percentage: 0 });
      },
      onSuccess: (result) => {
        setDownloadProgress(null);
        
        if (result.failed.length === 0) {
          toast.success(
            deviceInfo.isMobile || deviceInfo.isTablet 
              ? `${result.successful} photo(s) exportée(s) individuellement`
              : `${result.successful} photo(s) exportée(s) en archive`
          );
        } else {
          toast.success(`${result.successful} photo(s) exportée(s)`);
          if (result.failed.length > 0) {
            toast.error(`${result.failed.length} photo(s) ont échoué`);
          }
        }
        
        setSelectedPhotos([]);
      },
      onError: (error: any) => {
        setDownloadProgress(null);
        console.error('Admin download error:', error);
        toast.error(error.message || 'Erreur lors de l\'export des photos');
      },
    }
  );

  // Debug effect to log page changes
  useEffect(() => {
    console.log('🔄 Page changed to:', currentPage, 'Filters:', filters);
  }, [currentPage, filters]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) {
      toast.error('Aucun fichier sélectionné');
      return;
    }

    if (acceptedFiles.length > 50) {
      toast.error('Maximum 50 fichiers à la fois');
      return;
    }

    const validFiles = acceptedFiles.filter(file => 
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024
    );

    if (validFiles.length !== acceptedFiles.length) {
      toast.error('Certains fichiers ont été ignorés (format ou taille non valide)');
    }

    if (validFiles.length > 0) {
      uploadMutation.mutate(validFiles, {
        onSuccess: (data) => {
          toast.success(`${data.uploaded} photo(s) uploadée(s) avec succès`);
          if (data.failed > 0) {
            toast.error(`${data.failed} photo(s) ont échoué`);
          }
          setShowUpload(false);
        },
        onError: (error: any) => {
          toast.error(error.response?.data?.error || 'Erreur lors de l\'upload');
        },
      });
    }
  }, [uploadMutation]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.webp'],
    },
    maxSize: 10 * 1024 * 1024, // 10MB
    multiple: true,
  });

  const handlePhotoSelect = (photo: Photo) => {
    setSelectedPhotos(prev => {
      if (prev.includes(photo.id)) {
        return prev.filter(id => id !== photo.id);
      } else {
        return [...prev, photo.id];
      }
    });
  };

  const handleSelectAll = () => {
    if (!photosData?.data) return;
    
    const allCurrentPageIds = photosData.data.map(photo => photo.id);
    const allSelected = allCurrentPageIds.every(id => selectedPhotos.includes(id));
    
    if (allSelected) {
      setSelectedPhotos(prev => prev.filter(id => !allCurrentPageIds.includes(id)));
    } else {
      setSelectedPhotos(prev => [...new Set([...prev, ...allCurrentPageIds])]);
    }
  };

  const handleFilterChange = (newFilters: typeof filters) => {
    setFilters(newFilters);
    setCurrentPage(1);
    setSelectedPhotos([]);
  };

  const handleOpenTagModal = () => {
    // Get existing tags from selected photos
    const existingTagIds = getExistingTagsFromSelectedPhotos();
    setSelectedTagIds(existingTagIds);
    setShowTagModal(true);
  };

  const getExistingTagsFromSelectedPhotos = (): number[] => {
    if (!photosData?.data || selectedPhotos.length === 0) {
      return [];
    }

    const selectedPhotoObjects = photosData.data.filter(photo => 
      selectedPhotos.includes(photo.id)
    );

    if (selectedPhotoObjects.length === 0) {
      return [];
    }

    // For multiple photos, only return tags that are common to ALL selected photos
    if (selectedPhotoObjects.length === 1) {
      return selectedPhotoObjects[0].tags.map(tag => tag.id);
    }

    // Find intersection of tags across all selected photos
    const tagSets = selectedPhotoObjects.map(photo => 
      new Set(photo.tags.map(tag => tag.id))
    );

    const intersection = tagSets.reduce((acc, set) => 
      new Set([...acc].filter(tagId => set.has(tagId)))
    );

    return Array.from(intersection);
  };

  const getPhotosWithTag = (tagId: number): number => {
    if (!photosData?.data || selectedPhotos.length === 0) {
      return 0;
    }

    const selectedPhotoObjects = photosData.data.filter(photo => 
      selectedPhotos.includes(photo.id)
    );

    return selectedPhotoObjects.filter(photo => 
      photo.tags.some(tag => tag.id === tagId)
    ).length;
  };

  const handleCloseTagModal = () => {
    setShowTagModal(false);
    setSelectedTagIds([]);
    setShowCreateTagForm(false);
    setNewTagName('');
    setNewTagEmail('');
  };

  const handleTagSelection = (tagId: number) => {
    setSelectedTagIds(prev => 
      prev.includes(tagId) 
        ? prev.filter(id => id !== tagId)
        : [...prev, tagId]
    );
  };

  const handleApplyTags = async () => {
    try {
      await bulkTagMutation.mutateAsync({
        photoIds: selectedPhotos,
        tagIds: selectedTagIds,
        action: 'replace'
      });
      
      const message = selectedTagIds.length === 0 
        ? `Tags supprimés de ${selectedPhotos.length} photo(s)`
        : `${selectedPhotos.length} photo(s) taguée(s) avec succès`;
      
      toast.success(message);
      handleCloseTagModal();
      setSelectedPhotos([]);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors du tagging');
    }
  };

  const handleCreateTag = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newTagName.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      const createdTag = await createTagMutation.mutateAsync({
        name: newTagName.trim(),
        email: newTagEmail.trim() || undefined,
      });
      
      toast.success('Tag créé avec succès');
      
      // Automatically select the new tag
      setSelectedTagIds(prev => [...prev, createdTag.id]);
      
      // Reset form
      setNewTagName('');
      setNewTagEmail('');
      setShowCreateTagForm(false);
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const handleToggleCreateForm = () => {
    setShowCreateTagForm(!showCreateTagForm);
    if (showCreateTagForm) {
      setNewTagName('');
      setNewTagEmail('');
    }
  };

  const handleLightboxPhoto = (photo: Photo) => {
    setLightboxPhoto(photo);
  };

  const closeLightbox = () => {
    setLightboxPhoto(null);
  };

  const nextPhoto = () => {
    if (!lightboxPhoto || !photosData?.data) return;
    const currentIndex = photosData.data.findIndex(p => p.id === lightboxPhoto.id);
    const nextIndex = (currentIndex + 1) % photosData.data.length;
    setLightboxPhoto(photosData.data[nextIndex]);
  };

  const prevPhoto = () => {
    if (!lightboxPhoto || !photosData?.data) return;
    const currentIndex = photosData.data.findIndex(p => p.id === lightboxPhoto.id);
    const prevIndex = currentIndex === 0 ? photosData.data.length - 1 : currentIndex - 1;
    setLightboxPhoto(photosData.data[prevIndex]);
  };

  const handleAdminDownload = () => {
    if (selectedPhotos.length === 0) {
      toast.error('Veuillez sélectionner au moins une photo');
      return;
    }
    
    adminDownloadMutation.mutate(selectedPhotos);
  };

  if (photosLoading) {
    return <LoadingSpinner size="large" text="Chargement des photos..." />;
  }

  if (photosError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Erreur lors du chargement des photos</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 btn-primary"
        >
          Réessayer
        </button>
      </div>
    );
  }

  const photos = photosData?.data || [];
  const pagination = photosData?.pagination;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-gray-200 shadow-sm backdrop-blur-sm bg-white/95">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-bold text-gray-900">Galerie Photos</h1>
            <div className="mt-4 sm:mt-0 flex space-x-3">
              <div className="flex border border-gray-300 rounded-md">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'grid'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } rounded-l-md border-r border-gray-300`}
                  title="Vue grille"
                >
                  <Grid className="h-4 w-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'list'
                      ? 'bg-primary-600 text-white'
                      : 'bg-white text-gray-700 hover:bg-gray-50'
                  } rounded-r-md`}
                  title="Vue liste"
                >
                  <List className="h-4 w-4" />
                </button>
              </div>
              <button
                onClick={() => setShowUpload(!showUpload)}
                className="btn-primary"
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload Photos
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-6 space-y-6">
        {/* Upload Area */}
        {showUpload && (
          <div className="card p-6">
            <div
              {...getRootProps()}
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                isDragActive
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              <input {...getInputProps()} />
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-sm text-gray-600">
                {isDragActive
                  ? 'Déposez les fichiers ici...'
                  : 'Glissez-déposez vos photos ici, ou cliquez pour sélectionner'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PNG, JPG, WEBP jusqu'à 10MB (max 50 fichiers)
              </p>
            </div>
            
            {uploadMutation.isLoading && (
              <div className="mt-4">
                <LoadingSpinner text="Upload en cours..." />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Sticky Filters & Actions */}
      <div className="sticky top-[5.5rem] z-20 bg-white border-b border-gray-100 shadow-sm backdrop-blur-sm bg-white/95">
        <div className="px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div className="flex flex-wrap items-center gap-4">
              <Select
                value={filters.hasTag === undefined ? 'all' : filters.hasTag ? 'tagged' : 'untagged'}
                onChange={(value) => {
                  handleFilterChange({
                    hasTag: value === 'all' ? undefined : value === 'tagged' ? true : false,
                  });
                }}
                options={[
                  { value: 'all', label: 'Toutes les photos' },
                  { value: 'tagged', label: 'Avec tags' },
                  { value: 'untagged', label: 'Sans tags' }
                ]}
                className="w-auto min-w-[160px]"
                size="md"
                aria-label="Filtrer les photos par status de tag"
              />
            </div>

            {selectedPhotos.length > 0 && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-sm text-gray-600">
                    {selectedPhotos.length} photo(s) sélectionnée(s)
                  </span>
                  
                  {/* Device indicator for small screens */}
                  <div className="hidden sm:flex items-center text-xs text-gray-500">
                    {deviceInfo.isMobile ? (
                      <Smartphone className="h-3 w-3 mr-1" />
                    ) : (
                      <Monitor className="h-3 w-3 mr-1" />
                    )}
                    <span className="hidden lg:inline">
                      {deviceInfo.isMobile ? 'Mobile' : deviceInfo.isTablet ? 'Tablette' : 'PC'}
                    </span>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Download info button */}
                  <button
                    onClick={() => setShowDownloadInfo(!showDownloadInfo)}
                    className="btn-outline p-2 sm:px-3 sm:py-2"
                    title="Informations d'export"
                  >
                    <Info className="h-4 w-4" />
                    <span className="hidden sm:ml-2 sm:inline">Info</span>
                  </button>

                  {/* Download button */}
                  <button
                    onClick={handleAdminDownload}
                    disabled={adminDownloadMutation.isLoading || !!downloadProgress}
                    className="btn-secondary disabled:opacity-50 flex-shrink-0"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {adminDownloadMutation.isLoading || downloadProgress
                      ? (
                        <span className="flex items-center">
                          {downloadProgress ? (
                            <>
                              <span className="hidden sm:inline mr-2">
                                {downloadProgress.currentFile ? 
                                  `${downloadProgress.currentFile.substring(0, 12)}...` : 
                                  'Export'
                                }
                              </span>
                              <span>{downloadProgress.percentage}%</span>
                            </>
                          ) : (
                            'Préparation...'
                          )}
                        </span>
                      )
                      : (
                        <span>
                          <span className="hidden sm:inline">Exporter </span>
                          ({selectedPhotos.length})
                        </span>
                      )
                    }
                  </button>

                  <button 
                    onClick={handleOpenTagModal}
                    className="btn-primary"
                  >
                    Taguer
                  </button>
                  
                  <button 
                    onClick={() => setSelectedPhotos([])}
                    className="btn-outline"
                  >
                    <span className="hidden sm:inline">Désélectionner</span>
                    <X className="h-4 w-4 sm:hidden" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Photos Content Area */}
      <div className="px-4 sm:px-6 pt-4 sm:pt-6 pb-6 space-y-6">
        {/* Download Info Panel */}
        {showDownloadInfo && selectedPhotos.length > 0 && (
          <div className="card p-4 bg-blue-50 border-blue-200">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium text-blue-900 mb-2">
                  Informations d'export admin
                </h3>
                <div className="space-y-2 text-sm text-blue-800">
                  <div className="flex items-center">
                    {deviceInfo.isMobile ? (
                      <Smartphone className="h-4 w-4 mr-2" />
                    ) : (
                      <Monitor className="h-4 w-4 mr-2" />
                    )}
                    <span>{getAdminDownloadStrategyDescription()}</span>
                  </div>
                  
                  <div className="flex items-center">
                    <Download className="h-4 w-4 mr-2" />
                    <span>
                      {selectedPhotos.length} photo(s) sélectionnée(s) • 
                      Temps estimé: {estimateAdminDownloadTime(selectedPhotos.length)}
                    </span>
                  </div>
                  
                  {(deviceInfo.isMobile || deviceInfo.isTablet) && (
                    <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-100 rounded">
                      📱 <strong>Mode mobile détecté:</strong> Les photos seront exportées 
                      individuellement pour une meilleure compatibilité avec votre appareil.
                    </div>
                  )}
                  
                  {deviceInfo.isDesktop && (
                    <div className="text-xs text-blue-600 mt-2 p-2 bg-blue-100 rounded">
                      💻 <strong>Mode ordinateur:</strong> Les photos seront exportées 
                      dans une archive ZIP pour plus d'efficacité.
                    </div>
                  )}
                </div>
              </div>
              <button
                onClick={() => setShowDownloadInfo(false)}
                className="text-blue-400 hover:text-blue-600 ml-4"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Download Progress */}
        {downloadProgress && (
          <div className="card p-4 bg-green-50 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium text-green-900">
                Export en cours...
              </h3>
              <span className="text-sm text-green-700">
                {downloadProgress.completed}/{downloadProgress.total}
              </span>
            </div>
            
            <div className="w-full bg-green-200 rounded-full h-2 mb-2">
              <div 
                className="bg-green-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${downloadProgress.percentage}%` }}
              ></div>
            </div>
            
            {downloadProgress.currentFile && (
              <p className="text-xs text-green-600">
                📥 {downloadProgress.currentFile}
              </p>
            )}
          </div>
        )}

        {/* Photos Grid */}
      {photos.length === 0 ? (
        <div className="text-center py-12">
          <Upload className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune photo</h3>
          <p className="mt-1 text-sm text-gray-500">
            Commencez par uploader vos premières photos.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <button
              onClick={handleSelectAll}
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              {photos.every(photo => selectedPhotos.includes(photo.id))
                ? 'Désélectionner tout'
                : 'Sélectionner tout'}
            </button>
            
            {pagination && (
              <span className="text-sm text-gray-500">
                {pagination.total} photo(s) au total
              </span>
            )}
          </div>

          {viewMode === 'grid' ? (
            <div className="photo-grid">
              {photos.map((photo) => (
                <PhotoCard
                  key={photo.id}
                  photo={photo}
                  selected={selectedPhotos.includes(photo.id)}
                  onClick={() => handlePhotoSelect(photo)}
                  onLightboxClick={() => handleLightboxPhoto(photo)}
                  selectable
                  showTags
                  showLightboxButton
                />
              ))}
            </div>
          ) : (
            <div className="card overflow-hidden">
              <div className="space-y-1">
                {photos.map((photo, index) => (
                  <div key={photo.id} className={index > 0 ? 'border-t border-gray-100' : ''}>
                    <PhotoListItem
                      photo={photo}
                      selected={selectedPhotos.includes(photo.id)}
                      onClick={() => handlePhotoSelect(photo)}
                      onLightboxClick={() => handleLightboxPhoto(photo)}
                      selectable
                      showTags
                      showLightboxButton
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Précédent
          </button>
          
          <span className="text-sm text-gray-700">
            Page {currentPage} sur {pagination.pages}
          </span>
          
          <button
            onClick={() => setCurrentPage(prev => Math.min(pagination.pages, prev + 1))}
            disabled={currentPage === pagination.pages}
            className="btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Suivant
            <ChevronRight className="h-4 w-4 ml-2" />
          </button>
        </div>
      )}
      </div>

      {/* Tag Selection Modal */}
      {showTagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[32rem] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Sélectionner des tags
              </h3>
              <button 
                onClick={handleCloseTagModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-600">
                {selectedPhotos.length} photo(s) sélectionnée(s)
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Les tags cochés seront appliqués. Décochez pour supprimer un tag.
              </p>
            </div>

            {/* Create New Tag Section */}
            <div className="mb-4 border-b border-gray-200 pb-4">
              <button
                onClick={handleToggleCreateForm}
                className="flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                <Plus className="h-4 w-4 mr-1" />
                Créer un nouveau tag
              </button>
              
              {showCreateTagForm && (
                <form onSubmit={handleCreateTag} className="mt-3 space-y-3">
                  <input
                    type="text"
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    placeholder="Nom du tag (ex: Jean Dupont)"
                    className="input text-sm"
                    required
                  />
                  <input
                    type="email"
                    value={newTagEmail}
                    onChange={(e) => setNewTagEmail(e.target.value)}
                    placeholder="Email (optionnel)"
                    className="input text-sm"
                  />
                  <div className="flex space-x-2">
                    <button
                      type="submit"
                      disabled={createTagMutation.isLoading || !newTagName.trim()}
                      className="text-sm btn-primary disabled:opacity-50 px-3 py-1"
                    >
                      {createTagMutation.isLoading ? 'Création...' : 'Créer'}
                    </button>
                    <button
                      type="button"
                      onClick={handleToggleCreateForm}
                      className="text-sm btn-outline px-3 py-1"
                    >
                      Annuler
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Existing Tags Section */}
            <div className="mb-2">
              <h4 className="text-sm font-medium text-gray-700">Tags existants</h4>
            </div>
            
            <div className="space-y-2 mb-6">
              {tagsData?.data?.map((tag) => {
                const photosWithThisTag = getPhotosWithTag(tag.id);
                const somePhotosHaveThisTag = photosWithThisTag > 0 && photosWithThisTag < selectedPhotos.length;
                
                return (
                  <label
                    key={tag.id}
                    className="flex items-center space-x-3 p-2 rounded hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTagIds.includes(tag.id)}
                      onChange={() => handleTagSelection(tag.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      style={{
                        opacity: somePhotosHaveThisTag && !selectedTagIds.includes(tag.id) ? 0.6 : 1
                      }}
                    />
                    <span className="flex-1 text-sm text-gray-900">{tag.name}</span>
                    {tag.email && (
                      <span className="text-xs text-gray-500">({tag.email})</span>
                    )}
                    {photosWithThisTag > 0 && (
                      <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                        {photosWithThisTag}/{selectedPhotos.length}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>

            <div className="flex space-x-3">
              <button
                onClick={handleApplyTags}
                disabled={bulkTagMutation.isLoading}
                className="flex-1 btn-primary disabled:opacity-50"
              >
                {bulkTagMutation.isLoading ? (
                  'Application...'
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    {selectedTagIds.length === 0 
                      ? 'Supprimer tous les tags' 
                      : `Appliquer (${selectedTagIds.length})`
                    }
                  </>
                )}
              </button>
              <button
                onClick={handleCloseTagModal}
                className="btn-outline"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      <Lightbox
        photo={lightboxPhoto}
        photos={photos}
        onClose={closeLightbox}
        onNext={nextPhoto}
        onPrev={prevPhoto}
      />
    </div>
  );
}